import { z } from 'zod';
import { CommonConditionOperatorSchema } from './common-condition-operators.js';
export const RuleFieldSchema = z.enum([
    'document_name', 'title', 'content',
    'metadata_field', 'custom_property',
    'file_type', 'date', 'tag',
    // 0.1.11 — match by storage-container membership (by NAME, not id —
    // surviving container-name renames is operator-responsibility, but
    // the name in `value` is what seed bundles can carry portably).
    // Use with equals / contains / is_empty / is_not_empty. Lets the
    // Clipper nest auto-stamp `nest/clipper` on any doc routed to
    // `Clipper Storage` without depending on filename or frontmatter.
    'container',
    // 0.1.81 (CTSK-140 Stage 4b) — match by the doc's resolved baseline
    // type id (`getBaselineType(doc.mimeType).id`). String haystack
    // under equals / contains / starts_with / ends_with / regex_match.
    // Kept in lockstep with the `RuleField` union in
    // types/tagging-rule.ts.
    'baseline_type',
]);
// CTSK-442 — the tagging-rule operator vocabulary is now the shared
// CommonConditionOperatorSchema (14 operators: the original 9 plus the 5
// negated pairs `not_contains` / `not_equals` / `not_starts_with` /
// `not_ends_with` / `not_regex_match`). Strict superset of the previous
// enum — every pre-existing rule still validates. Negation semantics
// follow the CTSK-437 contract: positive = ANY-semantics, negated =
// strict boolean negation (universal quantifier).
export const RuleOperatorSchema = CommonConditionOperatorSchema;
export const RuleTriggerSchema = z.enum(['on_import', 'on_metadata_change', 'on_reprocess', 'manual']);
// CTSK-411 — which kind of entity a rule's conditions/actions are
// evaluated against. Absent on every pre-CTSK-411 bundle/row —
// TaggingRuleCreateSchema defaults it to 'document' so existing rules
// keep their current (and only) behavior. 'workitem' rules are matched
// by apps/api/src/lib/apply-rules-to-workitem.ts against a much
// narrower field vocabulary (WorkitemRuleFieldSchema below) — the
// document-side RuleFieldSchema and this new enum are deliberately
// kept as two separate vocabularies rather than one merged union, so
// neither engine has to guard against fields that can't apply to its
// entity type.
export const RuleScopeSchema = z.enum(['document', 'workitem']);
export const TaggingRuleConditionSchema = z.object({
    field: RuleFieldSchema,
    fieldKey: z.string().optional(),
    operator: RuleOperatorSchema,
    value: z.string().optional(),
});
// CTSK-411 — condition field vocabulary for scope: 'workitem' rules
// (v1). Kept intentionally small:
//   - `title`         — regex/string match against the work item's title.
//   - `label_present` — set-membership check against the work item's
//                        current `labels` (a flat string[], NOT the
//                        document-side tag-id system) — same ANY/
//                        universal-negation semantics as the document
//                        engine's `tag`/`container` fields.
//   - `state`          — match against the work item's `status` column.
// Assignee/dependency matchers are explicitly v2 (see ticket non-goals).
export const WorkitemRuleFieldSchema = z.enum(['title', 'label_present', 'state']);
// CTSK-411 — single condition shape for the workitem matcher. Reuses
// the same 14-operator RuleOperatorSchema vocabulary (and therefore
// the same @captain-tasko/shared applyOperator predicate table) as
// the document engine, so `not_*` universal-negation semantics are
// identical across both scopes — no second operator implementation to
// keep in lockstep.
export const WorkitemMatcherSchema = z.object({
    field: WorkitemRuleFieldSchema,
    operator: RuleOperatorSchema,
    value: z.string().optional(),
});
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const TaggingRuleCreateSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    // CTSK-411 — defaults to 'document' so every pre-existing bundle/row
    // (which omits this field entirely) keeps its current behavior
    // unchanged. Conditions here are still validated against the
    // document-only RuleFieldSchema regardless of scope — v1's direct
    // create/patch API doesn't accept workitem-field conditions yet
    // (bundle import + hand-SQL are the v1 paths for scope: 'workitem'
    // rows; see docs/rules.md).
    scope: RuleScopeSchema.default('document'),
    matchLogic: z.enum(['all', 'any']).default('all'),
    conditions: z.array(TaggingRuleConditionSchema).min(1).max(20),
    tagsToApply: z.array(z.string()).default([]),
    tagsToRemove: z.array(z.string()).default([]),
    // ID of a storage container to add matched documents to. null/undefined = no
    // container action for the rule.
    containerToAssign: z.string().min(1).max(26).nullable().optional(),
    triggers: z.array(RuleTriggerSchema).default(['on_import', 'manual']),
    priority: z.number().int().min(0).max(1000).default(10),
    enabled: z.boolean().default(true),
}).strict();
// .strip() restores Zod's default unknown-key tolerance for PATCH —
// CTSK-445 made Create strict, and .partial() would otherwise inherit
// `unknownKeys: "strict"`. Patch semantics deliberately unchanged.
export const TaggingRulePatchSchema = TaggingRuleCreateSchema.partial().strip();
export const TaggingRuleTestSchema = z.object({
    sampleSize: z.number().int().min(1).max(200).default(50).optional(),
});
//# sourceMappingURL=tagging-rule.js.map